"use client";

import { useEffect, useState, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { createClient } from "@/lib/supabase";
import type { BlogPost } from "@/types/database";
import { AnimateSection, StaggerContainer, StaggerItem } from "@/components/AnimateSection";

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from("blog_posts")
      .select("*")
      .eq("published", true)
      .order("published_at", { ascending: false })
      .then(({ data }) => {
        setPosts(data ?? []);
        setLoading(false);
      });
  }, []);

  // Extract all unique tags
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    posts.forEach((p) => {
      if (p.tags) {
        (p.tags as string[]).forEach((t) => tags.add(t));
      }
    });
    return Array.from(tags);
  }, [posts]);

  // Filter posts
  const filtered = useMemo(() => {
    return posts.filter((post) => {
      const matchesSearch =
        !search ||
        post.title.toLowerCase().includes(search.toLowerCase()) ||
        (post.excerpt ?? "").toLowerCase().includes(search.toLowerCase());
      const matchesTag =
        !activeTag ||
        (post.tags && (post.tags as string[]).includes(activeTag));
      return matchesSearch && matchesTag;
    });
  }, [posts, search, activeTag]);

  // Estimate reading time
  const readingTime = (content?: string | null) => {
    if (!content) return "1 min read";
    const words = content.split(/\s+/).length;
    const mins = Math.max(1, Math.ceil(words / 200));
    return `${mins} min read`;
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 sm:py-20">
        <h1 className="mb-8 text-4xl font-bold text-dev-text-primary">Blog</h1>
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="clay-card h-36 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:py-20">
      <AnimateSection>
        <h1 className="text-4xl font-bold text-dev-text-primary sm:text-5xl">
          <span className="text-gradient">Blog</span>
        </h1>
        <p className="mt-4 text-dev-text-muted">
          Thoughts, tutorials, and insights about software development
        </p>
      </AnimateSection>

      {/* Search */}
      <AnimateSection className="mt-8" delay={0.1}>
        <div className="relative">
          <svg
            className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dev-text-muted"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="clay-input w-full pl-11 pr-4 py-3 text-sm text-dev-text-primary placeholder-dev-text-muted"
          />
        </div>
      </AnimateSection>

      {/* Tags */}
      {allTags.length > 0 && (
        <AnimateSection className="mt-4" delay={0.15}>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTag(null)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${!activeTag
                  ? "bg-dev-blue/15 text-dev-cyan border border-dev-blue/30"
                  : "text-dev-text-muted hover:text-dev-text-secondary bg-white/5"
                }`}
            >
              All
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${activeTag === tag
                    ? "bg-dev-blue/15 text-dev-cyan border border-dev-blue/30"
                    : "text-dev-text-muted hover:text-dev-text-secondary bg-white/5"
                  }`}
              >
                #{tag}
              </button>
            ))}
          </div>
        </AnimateSection>
      )}

      {/* Posts */}
      <div className="mt-8">
        {filtered.length === 0 ? (
          <p className="py-12 text-center text-dev-text-secondary">
            {posts.length === 0
              ? "Chưa có bài viết nào."
              : "No articles found matching your search."}
          </p>
        ) : (
          <StaggerContainer as="ul" className="space-y-5">
            {filtered.map((post) => (
              <StaggerItem key={post.id} as="li">
                <Link
                  href={`/blog/${post.slug}/`}
                  className="clay-card group block cursor-pointer overflow-hidden transition-all duration-300 hover:border-dev-blue/20"
                >
                  <div className="flex flex-col sm:flex-row">
                    {post.cover_image_url && (
                      <div className="sm:w-48 flex-shrink-0">
                        <Image
                          src={post.cover_image_url}
                          alt={post.title}
                          width={400}
                          height={224}
                          unoptimized
                          className="h-40 w-full object-cover sm:h-full sm:rounded-l-[20px]"
                        />
                      </div>
                    )}
                    <div className="p-5 flex-1">
                      <h2 className="text-lg font-semibold text-dev-text-primary transition-colors group-hover:text-dev-cyan">
                        {post.title}
                      </h2>
                      {post.excerpt && (
                        <p className="mt-2 text-sm text-dev-text-secondary line-clamp-2">
                          {post.excerpt}
                        </p>
                      )}
                      <div className="mt-3 flex items-center gap-4 text-xs text-dev-text-muted">
                        {post.published_at && (
                          <span>
                            {new Date(post.published_at).toLocaleDateString("vi-VN", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        )}
                        <span>·</span>
                        <span>{readingTime(post.content)}</span>
                      </div>
                      {post.tags && (post.tags as string[]).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {(post.tags as string[]).slice(0, 3).map((tag) => (
                            <span key={tag} className="tech-tag text-[10px] !px-2 !py-0.5">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        )}
      </div>
    </div>
  );
}
